import type { Account } from '../../Account/Model/Account';
import type { User } from '../../User/Model/User';
import type { CreateSlotDTO, SimpleAccount, Slot } from '../Model/Slot';
import { calcRemaining } from '../Domain/UseCase/Utils/DateUtil';

function toSimpleAccount(account: Account): SimpleAccount {
  return {
    id: account.id,
    username: account.username,
    grade: account.grade,
    name: account.name,
  };
}

const EMPTY_ACCOUNT: SimpleAccount = {
  id: 0,
  username: '',
  grade: 'SELLER',
  name: '',
};

/** 슬롯 생성 API 응답 대기 중 목록에 즉시 표시할 임시 슬롯 */
export function buildOptimisticSlot(
  dto: CreateSlotDTO,
  tempId: number,
  me: User,
  accounts: Account[],
): Slot {
  const seller = accounts.find((a) => a.id === dto.sellerId);
  const sellerParent = seller?.parent
    ? accounts.find((a) => a.id === seller.parent)
    : undefined;

  return {
    slotId: tempId,
    sellerId: dto.sellerId,
    creator: me.id,
    creator_username: me.username ?? null,
    manager: dto.manager ?? null,
    user: dto.sellerId,
    user_username: seller?.username ?? null,
    parent: sellerParent ? toSimpleAccount(sellerParent) : EMPTY_ACCOUNT,
    user_parent: sellerParent ? toSimpleAccount(sellerParent) : EMPTY_ACCOUNT,
    slot_number: dto.slot_number,
    main_keyword: dto.main_keyword,
    word_keyword: dto.word_keyword ?? '-',
    mid_single: dto.mid_single ?? '',
    mid_compare: dto.mid_compare ?? '',
    product_url: dto.product_url ?? '',
    compare_url: dto.compare_url ?? '',
    memo: dto.memo,
    start_date: dto.start_date,
    end_date: dto.end_date,
    remaining_days: calcRemaining(dto.end_date),
    status: dto.status ?? '정상',
    today_rank: undefined,
    rank_check_status: 'pending',
    rank_calculating: true,
    is_optimistic: true,
  };
}

export function createOptimisticTempId(seed = Date.now(), index = 0): number {
  return -(seed * 1000 + index);
}

/** 일괄 생성 요청을 행 단위 DTO 배열로 펼침 */
export function expandBatchCreateDtos(request: {
  count: number;
  slots: CreateSlotDTO[];
}): CreateSlotDTO[] {
  const { count, slots } = request;
  if (!slots.length) return [];

  if (slots.length > 1) {
    return slots;
  }

  const template = slots[0];
  return Array.from({ length: count }, (_, i) => ({
    ...template,
    slot_number: template.slot_number || `batch-${i + 1}`,
  }));
}

export function prepareOptimisticBatchCreate(
  request: { count: number; slots: CreateSlotDTO[] },
  me: User,
  accounts: Account[],
): { optimisticSlots: Slot[]; tempIds: number[] } {
  const dtos = expandBatchCreateDtos(request);
  const seed = Date.now();
  const optimisticSlots: Slot[] = [];
  const tempIds: number[] = [];

  dtos.forEach((dto, i) => {
    const tempId = createOptimisticTempId(seed, i);
    tempIds.push(tempId);
    optimisticSlots.push(buildOptimisticSlot(dto, tempId, me, accounts));
  });

  return { optimisticSlots, tempIds };
}
